import { useEffect, useState } from 'react'
import useSearchroomChat from './useSearchroomChat'
import MessageBubble from './MessageBubble'
import ChatInput from './ChatInput'
import { Transcript, SearchBar, SearchResults } from './engine'
import { listModels } from '../../services/models'
import './SearchroomChat.css'

const ROLE_LABELS = { user: 'You', assistant: 'Codetalk' }

// The assistant "thinking" indicator shown while a reply streams in.
function ThinkingFooter() {
  return (
    <div className="msg-band msg-band-assistant">
      <div className="msg-band-inner">
        <div className="msg-role">Codetalk</div>
        <div className="msg-thinking">
          <span className="msg-dot" />
          <span className="msg-dot" />
          <span className="msg-dot" />
        </div>
      </div>
    </div>
  )
}

// Chat body for the searchroom's left pane: an optional search overlay above the
// transcript, then a docked composer. Search visibility is controlled by the
// header icon (lifted to SearchroomDisplay); everything else lives in the hook.
function SearchroomChat({ searchroomId, searchOpen, onCloseSearch, onOpenItem }) {
  const chat = useSearchroomChat(searchroomId)
  const { search } = chat
  const { close: closeSearch } = search
  const [models, setModels] = useState([])
  const [model, setModel] = useState('')

  useEffect(() => {
    let active = true
    listModels()
      .then(({ models: rows, default: fallback }) => {
        if (!active) return
        setModels(rows || [])
        setModel(fallback || (rows && rows[0] && rows[0].key) || '')
      })
      .catch(() => {})
    return () => {
      active = false
    }
  }, [])

  // Clear the query/results whenever the overlay is dismissed.
  useEffect(() => {
    if (!searchOpen) closeSearch()
  }, [searchOpen, closeSearch])

  return (
    <div className="searchroom-chat">
      <div className="searchroom-chat-main">
        {searchOpen && (
          <div className="chat-search-overlay">
            <SearchBar
              query={search.query}
              onQueryChange={search.setQuery}
              onSubmit={search.run}
              onClose={onCloseSearch}
              loading={search.loading}
            />
            <SearchResults
              results={search.results}
              query={search.query}
              loading={search.loading}
              error={search.error}
              ran={search.ran}
              onSelect={chat.selectResult}
              roleLabels={ROLE_LABELS}
            />
          </div>
        )}

        <Transcript
          messages={chat.messages}
          renderItem={(message, { highlighted }) => (
            <MessageBubble
              message={message}
              highlighted={highlighted}
              onOpenItem={onOpenItem}
              exportScope="searchroom"
              chatId={searchroomId}
            />
          )}
          loading={chat.loading}
          loadingLabel="Loading messages…"
          emptyLabel="Ask a question about this searchroom’s items."
          hasMoreBefore={chat.hasMoreBefore}
          hasMoreAfter={chat.hasMoreAfter}
          loadingOlder={chat.loadingOlder}
          loadingNewer={chat.loadingNewer}
          onLoadOlder={chat.loadOlder}
          onLoadNewer={chat.loadNewer}
          onJumpLatest={chat.reset}
          highlightId={chat.highlightId}
          footer={chat.sending ? <ThinkingFooter /> : null}
          resetKey={chat.resetKey}
          scrollToBottomSignal={chat.bottomSignal}
        />
      </div>

      {chat.error && <div className="searchroom-chat-error">{chat.error}</div>}

      <ChatInput
        placeholder="Message"
        onSend={(text, opts) => chat.send(text, { model, guides: opts?.guides })}
        disabled={chat.sending || !searchroomId}
        models={models}
        model={model}
        onModelChange={setModel}
      />
    </div>
  )
}

export default SearchroomChat
